/**
 * v133 — Photo-Anchored Logging overlay.
 *
 * Flow (mobile-first, full-screen):
 *   1. User picks a photo (camera or gallery) via <input type="file" capture>.
 *   2. Photo renders inside a fixed-aspect-ratio container that scales the
 *      photo to the container width while preserving aspect.
 *   3. CALIBRATE step: user taps two points on the photo.
 *        - Tap 1: bull center (POA → 0,0 in).
 *        - Tap 2: a known scoring-ring edge at a known radius (default 4″).
 *      A live preview shows concentric reference rings at 1″ / 2″ / 3″ … on
 *      top of the photo so the user can sanity-check before locking in.
 *   4. PLOT step: user taps each arrow hole on the photo. Each tap creates
 *      a pending dot at the inferred inches-from-POA. Drag to fine-tune
 *      (pointer move while still pressed); long-press to delete a placed dot.
 *   5. DONE: every placed dot is converted to RichShots in inches and the
 *      session commits them via the existing `onAddShot` API. The photo +
 *      calibration are stored on the current set so reopening the set in a
 *      future revision will re-render the calibration overlay.
 *
 * Coordinate systems
 * ──────────────────
 * - PHOTO PX  — pixel coordinates in the original photo (1080p typical)
 * - DISPLAY PX — pixel coordinates in the on-screen rendered container
 * - INCHES    — canonical storage (RichShot.hitXIn / hitYIn)
 *
 * We persist PHOTO PX on the set's calibration. Display-to-photo conversion
 * is a single multiply by `displayScale` derived from the rendered container
 * width vs the photo's natural width. This keeps the math stable across
 * device rotations and viewport resizes — re-rendering with a different
 * container size still produces the same canonical inches.
 */
import { useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/Button';
import { Camera, Image as ImageIcon, X, Crosshair, Target as TargetIcon, Trash2, Check, ArrowLeft, Sparkles, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import {
  pxToInches,
  inchesToPx,
  isCalibrationUsable,
  CALIB_RING_PRESETS,
} from '../photoCalibration';
import type { PhotoCalibration } from '../v3-schema';

type Stage = 'pick' | 'calibrate-bull' | 'calibrate-ring' | 'plot';

interface PendingShot {
  /** Stable local id (replaced with proper RichShot id on commit). */
  localId: string;
  /** Inches from POA — canonical. */
  xIn: number;
  yIn: number;
}

export interface PhotoTargetOverlayProps {
  /** Initial photo + calibration if the user is reopening an existing set. */
  initialPhotoDataUri?: string;
  initialCalibration?: PhotoCalibration;
  /** Existing shots already attached to this set — rendered for context. */
  existingShots?: ReadonlyArray<{ id: string; hitXIn: number; hitYIn: number }>;
  /** Default distance + target context used when committing new shots. */
  distanceYd: number;
  targetType: '3d' | 'paper' | '3-spot' | 'block' | 'custom';
  pin: string;
  /** Called when the user taps Done. Emits new shots + the photo + calib. */
  onCommit: (out: {
    newShots: Array<{ hitXIn: number; hitYIn: number }>;
    photoDataUri: string;
    calibration: PhotoCalibration;
  }) => void;
  /** Close the overlay without committing. */
  onCancel: () => void;
}

export function PhotoTargetOverlay({
  initialPhotoDataUri, initialCalibration, existingShots = [],
  distanceYd, targetType, pin,
  onCommit, onCancel,
}: PhotoTargetOverlayProps) {
  // Note: distanceYd / targetType / pin are passed through unchanged to the
  // parent on commit — the overlay itself doesn't read them, but they're in
  // the props for callsite ergonomics.
  void distanceYd; void targetType; void pin;
  const [stage, setStage] = useState<Stage>(initialPhotoDataUri ? 'plot' : 'pick');
  const [photoDataUri, setPhotoDataUri] = useState<string | undefined>(initialPhotoDataUri);
  const [photoSize, setPhotoSize] = useState<{ w: number; h: number } | null>(null);
  const [bullPx, setBullPx] = useState<{ x: number; y: number } | null>(
    initialCalibration ? initialCalibration.bullCenterPx : null,
  );
  const [calibPx, setCalibPx] = useState<{ x: number; y: number } | null>(
    initialCalibration ? initialCalibration.calibPointPx : null,
  );
  const [ringRadiusIn, setRingRadiusIn] = useState<number>(
    initialCalibration?.calibScoringRingIn ?? 4.0,
  );
  const [pendingShots, setPendingShots] = useState<PendingShot[]>([]);
  const [dragShotId, setDragShotId] = useState<string | null>(null);
  const longPressTimerRef = useRef<number | null>(null);
  const imgContainerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  // v134 — CV auto-detection state
  const [cvLoading, setCvLoading] = useState(false);
  const [cvError, setCvError] = useState<string | null>(null);

  // Lock body scroll while overlay is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Build a calibration object from current state (when both points + size exist).
  const calibration: PhotoCalibration | null = useMemo(() => {
    if (!bullPx || !calibPx || !photoSize) return null;
    return {
      bullCenterPx: bullPx,
      calibPointPx: calibPx,
      calibScoringRingIn: ringRadiusIn,
      photoWidthPx: photoSize.w,
      photoHeightPx: photoSize.h,
    };
  }, [bullPx, calibPx, ringRadiusIn, photoSize]);

  const calibUsable = !!calibration && isCalibrationUsable(calibration);

  // Helpers
  const displayToPhotoPx = (clientX: number, clientY: number): { x: number; y: number } | null => {
    const el = imgContainerRef.current;
    if (!el || !photoSize) return null;
    const rect = el.getBoundingClientRect();
    const dispX = clientX - rect.left;
    const dispY = clientY - rect.top;
    const scale = photoSize.w / rect.width;
    return { x: dispX * scale, y: dispY * scale };
  };

  // Handle file upload
  const onPickFile = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Probe natural dimensions
      const img = new window.Image();
      img.onload = () => {
        setPhotoSize({ w: img.naturalWidth, h: img.naturalHeight });
        setPhotoDataUri(result);
        setStage('calibrate-bull');
      };
      img.src = result;
    };
    reader.readAsDataURL(file);
  };

  // Handle photo container tap based on current stage
  const handlePhotoTap = (e: PointerEvent<HTMLDivElement>) => {
    const px = displayToPhotoPx(e.clientX, e.clientY);
    if (!px) return;

    if (stage === 'calibrate-bull') {
      setBullPx(px);
      setStage('calibrate-ring');
      return;
    }
    if (stage === 'calibrate-ring') {
      setCalibPx(px);
      return; // user confirms with the "Lock calibration" CTA
    }
    if (stage === 'plot' && calibration) {
      const inches = pxToInches(px, calibration);
      setPendingShots((prev) => [...prev, {
        localId: `pend_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
        xIn: round3(inches.xIn),
        yIn: round3(inches.yIn),
      }]);
    }
  };

  const handleDotPointerDown = (id: string) => {
    longPressTimerRef.current = window.setTimeout(() => {
      // Long press = delete
      setPendingShots((prev) => prev.filter((s) => s.localId !== id));
      longPressTimerRef.current = null;
    }, 600);
    setDragShotId(id);
  };
  const handleDotPointerMove = (e: PointerEvent<SVGCircleElement>) => {
    if (!dragShotId || !calibration) return;
    // Cancel long-press if moved
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    const px = displayToPhotoPx(e.clientX, e.clientY);
    if (!px) return;
    const inches = pxToInches(px, calibration);
    setPendingShots((prev) => prev.map((s) =>
      s.localId === dragShotId ? { ...s, xIn: round3(inches.xIn), yIn: round3(inches.yIn) } : s,
    ));
  };
  const handleDotPointerUp = () => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setDragShotId(null);
  };

  // Reference rings rendered on top of the photo during calibration so the
  // user can sanity-check that the locked scale matches reality.
  const referenceRings = useMemo(() => {
    if (!calibration) return [];
    const radii = [1, 2, 3, 4, 5, 6];
    return radii.map((rIn) => {
      const edge = inchesToPx({ xIn: rIn, yIn: 0 }, calibration);
      const r = Math.hypot(edge.x - calibration.bullCenterPx.x, edge.y - calibration.bullCenterPx.y);
      return { rIn, rPx: r };
    });
  }, [calibration]);

  /**
   * v134 — Auto-detect arrow holes via OpenCV.js.
   * Lazy-loads OpenCV on first call; subsequent calls reuse the runtime.
   * Detected candidates land as pending shots the user can confirm/reject.
   * Any failure surfaces a friendly error and the user falls back to manual tap.
   */
  const runAutoDetect = async () => {
    if (!calibration || !imgRef.current) return;
    setCvError(null);
    setCvLoading(true);
    try {
      const { detectArrowHoles } = await import('../cvRuntime');
      const candidates = await detectArrowHoles(imgRef.current, calibration);
      // Add detected candidates as pending shots (the user can drag / delete
      // them just like manually-placed dots before tapping Done).
      const newPending: PendingShot[] = candidates.map((c) => ({
        localId: `pend_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
        xIn: round3(c.inches.xIn),
        yIn: round3(c.inches.yIn),
      }));
      setPendingShots((prev) => [...prev, ...newPending]);
      if (newPending.length === 0) {
        setCvError('No arrow holes detected. Try better lighting or tap each hole manually.');
      }
    } catch (e) {
      setCvError(e instanceof Error ? e.message : 'Auto-detect failed. Tap each hole manually.');
    } finally {
      setCvLoading(false);
    }
  };

  const commit = () => {
    if (!photoDataUri || !calibration || pendingShots.length === 0) return;
    onCommit({
      newShots: pendingShots.map((s) => ({ hitXIn: s.xIn, hitYIn: s.yIn })),
      photoDataUri,
      calibration,
    });
  };

  // Empty state — no photo yet
  if (stage === 'pick') {
    return createPortal(
      <div className="fixed inset-0 z-[9000] bg-black/95 backdrop-blur-md flex flex-col" style={{ height: '100dvh' }}>
        <header className="flex-shrink-0 px-5 py-3 flex items-center justify-between border-b border-zinc-800">
          <h2 className="text-base font-bold text-zinc-100 flex items-center gap-2">
            <ImageIcon size={16} className="text-ember-500" /> Photo target overlay
          </h2>
          <button onClick={onCancel} className="w-10 h-10 flex items-center justify-center text-zinc-500 hover:text-zinc-100 touch-manipulation" aria-label="Close">
            <X size={18} />
          </button>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center p-6 gap-4 text-center">
          <p className="text-sm text-zinc-300 max-w-md">
            Take a photo of the target square-on, then calibrate it to inches by tapping the bull and a known scoring ring. After that, tap each arrow hole and we&rsquo;ll log them.
          </p>
          <label className="block w-full max-w-xs">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="sr-only"
              onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
            />
            <span className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-ember-500 text-zinc-950 rounded-md font-semibold min-h-[48px] cursor-pointer touch-manipulation">
              <Camera size={16} /> Take or pick a photo
            </span>
          </label>
          <p className="text-[11px] text-zinc-500 max-w-md">
            Tip: shoot square to the face, fill the frame with the scoring area, and keep the photo level. Linear scale; v133 doesn&rsquo;t correct rotation or perspective skew.
          </p>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[9000] bg-black/95 backdrop-blur-md flex flex-col" style={{ height: '100dvh' }}>
      {/* ───── HEADER ───── */}
      <header className="flex-shrink-0 px-3 py-2 flex items-center justify-between border-b border-zinc-800 gap-2">
        <button onClick={onCancel} className="w-10 h-10 flex items-center justify-center text-zinc-500 hover:text-zinc-100 touch-manipulation" aria-label="Cancel">
          <ArrowLeft size={18} />
        </button>
        <div className="text-center min-w-0">
          <p className="text-[10px] font-mono uppercase tracking-wider text-cyan-400">
            {stage === 'calibrate-bull' ? 'Step 1 of 3 · Tap the bull' :
             stage === 'calibrate-ring' ? 'Step 2 of 3 · Tap a known scoring ring' :
             'Step 3 of 3 · Tap each arrow hole'}
          </p>
          <p className="text-sm font-semibold text-zinc-100 truncate">
            {stage === 'plot' ? 'Plot arrows' : 'Calibrate'}
          </p>
        </div>
        <div className="w-10" />
      </header>

      {/* ───── PHOTO + OVERLAYS ───── */}
      <div className="flex-1 min-h-0 overflow-auto p-2">
        <div
          ref={imgContainerRef}
          onPointerDown={handlePhotoTap}
          className="relative w-full mx-auto bg-zinc-950 border border-zinc-800 rounded-md overflow-hidden touch-manipulation select-none"
          style={photoSize ? { aspectRatio: `${photoSize.w} / ${photoSize.h}`, maxWidth: 720 } : undefined}
        >
          {photoDataUri && (
            <img
              ref={imgRef}
              src={photoDataUri}
              alt="Target"
              draggable={false}
              crossOrigin="anonymous"
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            />
          )}

          {/* Overlay SVG renders in photo-px coordinates and scales with the container. */}
          {photoSize && (
            <svg
              viewBox={`0 0 ${photoSize.w} ${photoSize.h}`}
              preserveAspectRatio="none"
              className="absolute inset-0 w-full h-full pointer-events-none"
              aria-hidden
            >
              {/* Bull crosshair */}
              {bullPx && (
                <g>
                  <line x1={bullPx.x - 20} y1={bullPx.y} x2={bullPx.x + 20} y2={bullPx.y} stroke="#22d3ee" strokeWidth={1.5} />
                  <line x1={bullPx.x} y1={bullPx.y - 20} x2={bullPx.x} y2={bullPx.y + 20} stroke="#22d3ee" strokeWidth={1.5} />
                  <circle cx={bullPx.x} cy={bullPx.y} r={6} fill="none" stroke="#22d3ee" strokeWidth={1.5} />
                </g>
              )}
              {/* Calibration ring tap */}
              {calibPx && (
                <g>
                  <circle cx={calibPx.x} cy={calibPx.y} r={8} fill="#22d3ee" opacity={0.85} />
                  <text x={calibPx.x + 12} y={calibPx.y - 8} fill="#22d3ee" fontSize={18} fontFamily="ui-monospace,monospace">
                    {ringRadiusIn.toFixed(1)}″
                  </text>
                </g>
              )}
              {/* Reference rings to sanity-check the calibration */}
              {referenceRings.length > 0 && bullPx && (
                <g>
                  {referenceRings.map(({ rIn, rPx }) => (
                    <circle key={rIn} cx={bullPx.x} cy={bullPx.y} r={rPx}
                      fill="none" stroke="#fb923c" strokeWidth={0.8}
                      strokeDasharray="6 4" opacity={0.55} />
                  ))}
                </g>
              )}
              {/* Existing shots from prior commits — ember outline */}
              {calibration && existingShots.map((s) => {
                const p = inchesToPx({ xIn: s.hitXIn, yIn: s.hitYIn }, calibration);
                return (
                  <circle key={s.id} cx={p.x} cy={p.y} r={5}
                    fill="none" stroke="#fb923c" strokeWidth={1.6} />
                );
              })}
              {/* Pending shots — interactive */}
              {calibration && pendingShots.map((s, i) => {
                const p = inchesToPx({ xIn: s.xIn, yIn: s.yIn }, calibration);
                return (
                  <g key={s.localId}>
                    <circle
                      cx={p.x} cy={p.y} r={8}
                      fill="#22d3ee" stroke="#fafafa" strokeWidth={1.6}
                      style={{ pointerEvents: 'all', cursor: 'grab' }}
                      onPointerDown={(e) => { e.stopPropagation(); handleDotPointerDown(s.localId); }}
                      onPointerMove={handleDotPointerMove}
                      onPointerUp={handleDotPointerUp}
                      onPointerCancel={handleDotPointerUp}
                    />
                    <text x={p.x + 11} y={p.y + 5} fill="#22d3ee" fontSize={14} fontFamily="ui-monospace,monospace" style={{ pointerEvents: 'none' }}>
                      {i + 1}
                    </text>
                  </g>
                );
              })}
            </svg>
          )}
        </div>
      </div>

      {/* ───── CONTROLS — varies by stage ───── */}
      <footer className="flex-shrink-0 px-3 py-3 border-t border-zinc-800 bg-zinc-950 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {stage === 'calibrate-bull' && (
          <p className="text-[12px] text-zinc-400 text-center">
            Tap the very center of the bull / X-ring. This becomes your point of aim.
          </p>
        )}
        {stage === 'calibrate-ring' && (
          <div className="space-y-2">
            <p className="text-[12px] text-zinc-400 text-center">
              Tap a known scoring-ring edge, then confirm the reference radius below.
            </p>
            <div className="flex items-center gap-2 flex-wrap justify-center">
              {CALIB_RING_PRESETS.slice(0, 4).map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => setRingRadiusIn(preset.radiusIn)}
                  className={clsx(
                    'px-2 py-1.5 rounded border text-[11px] font-mono uppercase tracking-wider touch-manipulation',
                    Math.abs(ringRadiusIn - preset.radiusIn) < 0.01
                      ? 'border-cyan-500/60 bg-cyan-500/10 text-cyan-300'
                      : 'border-zinc-800 text-zinc-400 hover:border-zinc-600',
                  )}
                >{preset.label}</button>
              ))}
              <input
                type="number" step={0.1} min={0.1}
                value={ringRadiusIn}
                onChange={(e) => setRingRadiusIn(parseFloat(e.target.value) || 4)}
                className="w-20 bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm font-mono tabular-nums"
                aria-label="Reference radius in inches"
              />
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <Button variant="secondary" size="md" onClick={() => { setBullPx(null); setCalibPx(null); setStage('calibrate-bull'); }}>
                Restart calibration
              </Button>
              <Button size="md" onClick={() => setStage('plot')} disabled={!calibUsable}>
                <Crosshair size={14} className="mr-1.5" /> Lock & plot
              </Button>
            </div>
          </div>
        )}
        {stage === 'plot' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-[12px]">
              <span className="text-zinc-400">
                {pendingShots.length === 0
                  ? 'Tap each arrow hole. Drag to fine-tune. Long-press to delete.'
                  : `${pendingShots.length} pending shot${pendingShots.length === 1 ? '' : 's'}`}
              </span>
              {pendingShots.length > 0 && (
                <button
                  onClick={() => setPendingShots([])}
                  className="text-[11px] text-zinc-500 hover:text-rose-400 flex items-center gap-1 touch-manipulation"
                >
                  <Trash2 size={11} /> Clear
                </button>
              )}
            </div>
            {/* v134 — CV auto-detect entry */}
            {cvError && (
              <p className="text-[11px] text-rose-400 bg-rose-950/20 border border-rose-700/40 rounded px-2 py-1">
                {cvError}
              </p>
            )}
            <div className="flex gap-2 flex-wrap justify-end">
              <button
                onClick={runAutoDetect}
                disabled={cvLoading}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 min-h-[44px] text-[12px] font-mono uppercase tracking-wider border border-cyan-500/50 text-cyan-300 rounded hover:bg-cyan-500/10 disabled:opacity-50 touch-manipulation"
                title="Detect arrow holes via computer vision"
              >
                {cvLoading
                  ? <><Loader2 size={12} className="animate-spin" /> Detecting…</>
                  : <><Sparkles size={12} /> Auto-detect</>
                }
              </button>
              <Button variant="secondary" size="md" onClick={() => setStage('calibrate-ring')}>
                <TargetIcon size={14} className="mr-1.5" /> Recalibrate
              </Button>
              <Button size="md" onClick={commit} disabled={pendingShots.length === 0}>
                <Check size={14} className="mr-1.5" /> Done · log {pendingShots.length}
              </Button>
            </div>
          </div>
        )}
      </footer>
    </div>,
    document.body,
  );
}

function round3(n: number): number { return Math.round(n * 1000) / 1000; }
